import { makeFetch } from './utils';
const delay = (ms) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export class GitHubAPIHelper {
  constructor(host, repo, owner, branch) {
    this._host = host;
    this._repo = repo;
    this._owner = owner;
    this._branch = branch;
  }

  /**
   * Performs a look up using github api, at the specified path.
   * @param {string} path The path on github to the folder you are looking up.
   * @return {*} Returns a github file or directory object, see github api for details
   */
  getContent = (path) => {
    return makeFetch(
      '',
      `api/content?host=${this._host}&&owner=${this._owner}&&repo=${
        this._repo
      }&&path=${encodeURIComponent(path)}`
    );
  };

  /**
   * Gets the whole tree with github tree api.
   * @return {*} Returns a github tree object, see github tree api for details
   */
  getTree = () => {
    return makeFetch(
      '',
      `api/tree?host=${this._host}&&owner=${this._owner}&&repo=${this._repo}&&branch=${this._branch}`
    );
  };

  /**
   * Performs a recursive, depth first search finding the content of all files in a git directory.
   * @param {string} path The path on github to the folder you are searching to find all files within.
   * @param {*} schemas The  output of the function, a list of all schemas found.
   * @return {*} Returns promise containing nothing useful.
   */
  depthFirstRetrieveAllSchemas = (path, schemas) => {
    if (path instanceof Array) {
      const promises = [];
      for (let i = 0; i < path.length; ++i) {
        promises.push(this.depthFirstRetrieveAllSchemas(path[i], schemas));
      }
      return Promise.all(promises);
    }
    return this.getContent(path).then((content) => {
      const promises = [];
      if (content.data.type === 'file') {
        return this.getContent(content.data.path).then((res) => {
          if (res.data.name.includes('.json')) {
            schemas.push(res.data);
          }
        });
      } else {
        content.data.forEach((child) => {
          if (child.type === 'dir') {
            promises.push(
              delay(Math.floor(Math.random() * 10000) + 10000).then(() => {
                return this.depthFirstRetrieveAllSchemas(child.path, schemas);
              })
            );
          } else {
            promises.push(
              delay(Math.floor(Math.random() * 10000) + 1000)
                .then(() => {
                  return this.getContent(child.path);
                })
                .then((res) => {
                  if (res.data.name.includes('.json')) {
                    schemas.push(res.data);
                  }
                })
            );
          }
        });
      }
      return Promise.all(promises);
    });
  };

  /**
   * Finding the content of all files in a git directory, not include the files in the sub-folders.
   * @param {string} path The path on github to the folder.
   * @param {*} schemas The  output of the function, a list of all schemas found.
   * @return {*} Returns promise containing nothing useful.
   */
  getSchemasOfFolder = (path, schemas) => {
    if (path instanceof Array) {
      const promises = [];
      for (let i = 0; i < path.length; ++i) {
        promises.push(this.getSchemasOfFolder(path[i], schemas));
      }
      return Promise.all(promises);
    }
    return this.getContent(path).then((content) => {
      const promises = [];
      if (content.data.type === 'file') {
        return this.getContent(content.data.path).then((res) => {
          if (res.data.name.includes('.json')) {
            schemas.push(res.data);
          }
        });
      } else {
        content.data.forEach((child) => {
          if (child.type === 'file') {
            promises.push(
              delay(Math.floor(Math.random() * 10000) + 1000)
                .then(() => {
                  return this.getContent(child.path);
                })
                .then((res) => {
                  if (res.data.name.includes('.json')) {
                    schemas.push(res.data);
                  }
                })
            );
          }
        });
      }
      return Promise.all(promises);
    });
  };

  /**
   * Performs a lookup to github api, finds all files being touched by a pull request.
   * @param {string} number The number which identifies the pr.
   * @param {*} schemas The  output of the function, a list of all schemas found.
   * @return {*} Returns an array of files descriptions, see github api for details
   */
  getPullRequestFiles = (number, schemas) => {
    return this._gh.pullRequests
      .listFiles({
        owner: this._owner,
        repo: this._repo,
        number,
      })
      .then((filesData) => {
        const promises = [];
        const files = filesData.data;
        files.forEach((child) => {
          if (child.status === 'removed') {
            return;
          }
          promises.push(
            delay(Math.floor(Math.random() * 10000) + 1000)
              .then(() => {
                return this.getContent(child.filename);
              })
              .then((res) => {
                if (res.data.name.includes('.json')) {
                  schemas.push(res.data);
                }
              })
          );
        });
        return Promise.all(promises);
      });
  };
}

export default GitHubAPIHelper;
